import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoUpim } from '../components/ui-upim';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { accessDestination } from '../services/accessDestination';
import { ShieldCheck, KeyRound, Copy, Check } from 'lucide-react';

export default function CuentaSeguridadPage() {
  const { user, changePasswordRequired, enableTotp, logout } = useAuth();
  const obligatorio = !!user?.must_change_password;
  const activo = !!user?.totp_enabled;
  const [editarPassword, setEditarPassword] = useState(false);
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetir, setRepetir] = useState('');
  const [cambiada, setCambiada] = useState(false);
  const [setup, setSetup] = useState(null);
  const [codigosRespaldo, setCodigosRespaldo] = useState([]);
  const [codigo, setCodigo] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const copyTimer = useRef(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const cambiar = async (event) => {
    event.preventDefault();
    if (nueva !== repetir) { toast({ title: 'Las contraseñas no coinciden', variant: 'destructive' }); return; }
    setCargando(true);
    try {
      await changePasswordRequired(actual, nueva);
      setActual(''); setNueva(''); setRepetir(''); setEditarPassword(false); setCambiada(true);
      toast({ title: 'Contraseña actualizada', description: 'Las sesiones anteriores quedaron invalidadas.' });
    } catch (error) { toast({ title: 'No se pudo cambiar', description: error.message, variant: 'destructive' }); }
    finally { setCargando(false); }
  };
  const iniciar2FA = async () => {
    setCargando(true);
    try {
      const response = await authAPI.totpSetup();
      setSetup(response); setCodigosRespaldo(response.backup_codes); setCopiado(false);
    } catch (error) { toast({ title: 'No se pudo configurar 2FA', description: error.message, variant: 'destructive' }); }
    finally { setCargando(false); }
  };
  const activar2FA = async (event) => {
    event.preventDefault(); setCargando(true);
    try {
      await enableTotp(codigo);
      setSetup(null); setCodigo('');
      toast({ title: '2FA activado', description: 'Tu cuenta ya exige verificación en dos pasos.' });
    } catch (error) { toast({ title: 'No se pudo activar 2FA', description: error.message, variant: 'destructive' }); }
    finally { setCargando(false); }
  };
  const copiarCodigos = async () => {
    try {
      await navigator.clipboard.writeText(codigosRespaldo.join('\n'));
      setCopiado(true); clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiado(false), 2500);
    } catch (_) { toast({ title: 'No se pudieron copiar los códigos', description: 'Guárdalos manualmente en un lugar privado.', variant: 'destructive' }); }
  };
  const irAlPanel = async () => {
    setCargando(true);
    try { navigate(await accessDestination(user)); }
    finally { setCargando(false); }
  };
  const cardClass = 'border-[var(--upim-borde)] bg-[var(--upim-superficie-2)]';
  return <div className="min-h-screen bg-black px-4 py-8 text-[var(--upim-texto)] sm:py-12">
    <div className="mx-auto max-w-2xl space-y-6" data-testid="cuenta-seguridad">
      <div className="flex items-center justify-between gap-4">
        <Link to="/" aria-label="Ir al inicio"><LogoUpim className="h-10" /></Link>
        <Button variant="ghost" onClick={logout}>Cerrar sesión</Button>
      </div>
      <div><h1 className="font-display text-3xl font-bold">Seguridad de la cuenta</h1>
        <p className="mt-2 text-[var(--upim-texto-2)]">Protege tu acceso y consulta el estado de la verificación en dos pasos.</p></div>
      <Card className={cardClass}>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="h-5 w-5" />{obligatorio ? 'Cambio de contraseña obligatorio' : 'Contraseña de tu cuenta'}</CardTitle></CardHeader>
        <CardContent>
          {cambiada && <p role="status" data-testid="password-cambiada" className="mb-4 text-sm text-emerald-400">Contraseña actualizada. Las sesiones anteriores fueron invalidadas.</p>}
          {obligatorio || editarPassword ? <form onSubmit={cambiar} className="space-y-4" data-testid="form-cambio-password">
            <div><Label htmlFor="security-current">Contraseña actual</Label><Input id="security-current" type="password" autoComplete="current-password" value={actual} onChange={e=>setActual(e.target.value)} required data-testid="password-actual" /></div>
            <div><Label htmlFor="security-new">Nueva contraseña</Label><Input id="security-new" type="password" autoComplete="new-password" minLength={12} value={nueva} onChange={e=>setNueva(e.target.value)} required data-testid="password-nueva" /><p className="mt-1 text-xs text-[var(--upim-texto-2)]">Mínimo 12 caracteres, con mayúsculas, minúsculas y al menos un número.</p></div>
            <div><Label htmlFor="security-repeat">Repetir nueva contraseña</Label><Input id="security-repeat" type="password" autoComplete="new-password" value={repetir} onChange={e=>setRepetir(e.target.value)} required data-testid="password-repetir" /></div>
            <Button type="submit" disabled={cargando} data-testid="password-guardar">Actualizar contraseña</Button>
          </form> : <div className="space-y-3"><p className="text-sm text-[var(--upim-texto-2)]">Tu contraseña está configurada.</p><Button variant="outline" onClick={()=>setEditarPassword(true)}>Cambiar contraseña</Button></div>}
        </CardContent>
      </Card>
      <Card className={cardClass}>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="h-5 w-5" />Verificación en dos pasos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {activo ? <p role="status" data-testid="totp-activo" className="text-sm text-emerald-400">2FA activo. En cada inicio de sesión se pedirá el código de tu aplicación.</p> : !setup ? <Button onClick={iniciar2FA} disabled={cargando || obligatorio} data-testid="totp-iniciar">{obligatorio ? 'Primero cambia tu contraseña' : 'Configurar 2FA'}</Button> : <div className="space-y-4">
            <p className="text-sm text-[var(--upim-texto-2)]">Escanea el código con Google Authenticator, Microsoft Authenticator o 1Password.</p>
            <img data-testid="totp-qr" alt="Código QR para 2FA" src={`data:image/png;base64,${setup.qr_png_base64}`} className="h-44 w-44 rounded-lg bg-white p-2" />
            <form onSubmit={activar2FA} className="flex flex-wrap items-end gap-3" data-testid="form-totp">
              <div className="min-w-0 flex-1"><Label htmlFor="security-totp">Código de 6 dígitos</Label><Input id="security-totp" value={codigo} onChange={e=>setCodigo(e.target.value)} maxLength={6} pattern="[0-9]{6}" required inputMode="numeric" autoComplete="one-time-code" placeholder="000000" data-testid="totp-codigo" /></div>
              <Button type="submit" disabled={cargando} data-testid="totp-activar">Activar 2FA</Button>
            </form>
          </div>}
          {codigosRespaldo.length > 0 && <div className="space-y-3">
            <p className="text-sm font-semibold">Códigos de respaldo</p><p className="text-sm text-[var(--upim-texto-2)]">Guárdalos en un lugar privado. No podrás volver a consultar estos mismos códigos.</p>
            <div data-testid="totp-backup-codes" className="grid grid-cols-2 gap-2 rounded border border-[var(--upim-borde)] p-3 font-mono text-xs">{codigosRespaldo.map(code=><span key={code}>{code}</span>)}</div>
            <div className="flex flex-wrap gap-2"><Button variant="outline" size="sm" onClick={copiarCodigos} data-testid="totp-copiar-codigos">{copiado ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}{copiado ? 'Copiados' : 'Copiar códigos'}</Button>
              <Button variant="ghost" size="sm" onClick={()=>setCodigosRespaldo([])}>Ya guardé estos códigos</Button></div>
          </div>}
        </CardContent>
      </Card>
      {!obligatorio && <Button onClick={irAlPanel} disabled={cargando} data-testid="ir-al-panel">Ir a mi panel</Button>}
    </div>
  </div>;
}
